// Comment templates for the "reply under the Facebook group post" flow. Shared by the admin reviews
// page, the moderator Comment queue (Phase 45) and the store-page admin FB comment button, so every
// surface pastes identical sentences.
//
// Templates carry a tone. An apology pasted under a 5-star review reads as sarcasm (and a cheerful
// sentence under a scam complaint reads as tone-deaf), so the random DEFAULT only draws apologies for
// negative reviews and only draws general sentences otherwise. The moderator picker still lists every
// template, so a moderator can override the default deliberately.

export const commentTemplateTones = { any: "any", negative: "negative" } as const;
export type CommentTemplateTone = keyof typeof commentTemplateTones;

export type CommentTemplateEntry = { text: string; tone: CommentTemplateTone };

export const COMMENT_TEMPLATE_ENTRIES: CommentTemplateEntry[] = [
  { text: "مراجعتك اتسجلت على يتفرج 🎉 شوف المتجر هنا: {link}", tone: "any" },
  { text: "رأيك وصل وتم نشره على يتفرج ✅ {link}", tone: "any" },
  { text: "شكراً على مراجعتك! هتلاقيها على يتفرج: {link}", tone: "any" },
  { text: "تم إضافة مراجعتك على يتفرج 👍 {link}", tone: "any" },
  { text: "حفظنا رأيك على يتفرج! {link}", tone: "any" },
  { text: "مراجعتك موجودة دلوقتي على يتفرج: {link}", tone: "any" },
  { text: "تم حفظ مراجعتك 🙏 {link}", tone: "any" },
  {
    text: "تم حفظ تجربتك على يتفرج، منصة بتجمع التجارب والمراجعات لمساعدة الناس قبل الشراء. تقدر تشوفها من هنا: {link}",
    tone: "any",
  },
  {
    text: "آسفين إنك مريت بالتجربة دي احنا بنجمع على يتفرج الريفيوز عشان تكون متاحة للناس قبل ما تشتري تقدر تشوف تجربتك من هنا: {link}",
    tone: "negative",
  },
  {
    text: "آسفين إنك مريت بالتجربة دي إحنا في يتفرج بنوفر مساحة للعملاء يشاركوا تجاربهم عشان تكون متاحة لأي حد بيدور عن معلومات قبل ما يشتري. حفظنا تجربتك على موقعنا، وتقدر تشوفها من هنا: {link}",
    tone: "negative",
  },
];

export const COMMENT_TEMPLATES: string[] = COMMENT_TEMPLATE_ENTRIES.map((e) => e.text);

export const GENERAL_COMMENT_TEMPLATES: string[] = COMMENT_TEMPLATE_ENTRIES.filter(
  (e) => e.tone === commentTemplateTones.any,
).map((e) => e.text);

export const NEGATIVE_COMMENT_TEMPLATES: string[] = COMMENT_TEMPLATE_ENTRIES.filter(
  (e) => e.tone === commentTemplateTones.negative,
).map((e) => e.text);

// The backend maps FB sentiment to rating (negative 2, mixed/neutral 3, positive 4), so a 1 to 2 rating
// counts as negative even without a label. mixed/neutral are not negative; rating 0 means unset.
// The index signature keeps the store-page Review (sourceData is an open record) assignable; without it
// TypeScript's weak-type check rejects a sourceData with no declared key in common.
export const isNegativeReview = (review: {
  overallRating?: number;
  sourceData?: { sentimentLabel?: unknown; [key: string]: unknown } | null;
}): boolean =>
  review.sourceData?.sentimentLabel === "negative" ||
  (typeof review.overallRating === "number" && review.overallRating > 0 && review.overallRating <= 2);

// With no argument this never returns an apology (back-compat for callers that know nothing about the
// review's tone).
export const pickCommentTemplate = ({ negative = false }: { negative?: boolean } = {}): string => {
  const pool = negative ? NEGATIVE_COMMENT_TEMPLATES : GENERAL_COMMENT_TEMPLATES;
  return pool[Math.floor(Math.random() * pool.length)];
};

export const pickCommentTemplateFor = (review: Parameters<typeof isNegativeReview>[0]): string =>
  pickCommentTemplate({ negative: isNegativeReview(review) });

// Position of a template in COMMENT_TEMPLATES (-1 if absent). The moderator template picker uses it
// to highlight the template currently chosen for a review.
export const templateIndexOf = (template: string): number => COMMENT_TEMPLATES.indexOf(template);

// Template rendering is split from template PICKING so the same sentence can be re-rendered with a
// different link. handleCommentFlow writes the long link synchronously and then rewrites the
// clipboard with the short one — re-randomizing would swap the pasted sentence mid-flight.
export const renderCommentText = ({
  template,
  storeSlug,
  allowExternalLinksInComments,
  shortUrl,
}: {
  template: string;
  storeSlug: string;
  allowExternalLinksInComments: boolean;
  shortUrl?: string | null;
}): string => {
  // The long fallback deliberately carries no utm params — it is a human-readable fallback; the
  // short link is what carries attribution.
  let display =
    typeof shortUrl === "string" && shortUrl.length > 0
      ? shortUrl.replace(/^https?:\/\//, "")
      : `yetfarrag.com/store/${storeSlug}`;
  // Groups that block external links get the existing spaced form (`ytfrg .com/<code>`).
  if (!allowExternalLinksInComments) display = display.replace(".com", " .com");
  return template.replace("{link}", display);
};
